import React from 'react';

import { StyleSheet } from 'react-native';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { Box, Typography } from 'components/ui';
import { useComposeDescription } from 'hooks/use-compose-description';
import { spacing } from 'styles/constants';
import { getDesignToken } from 'styles/constants';
import { CartEntryType } from 'utils/cart/types';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';

interface ILegacyCartEntry {
  entry: IBackendCartEntries;
  currencyFormatter: Function;
  index?: number;
}

const CartEntry = ({ entry, currencyFormatter }: ILegacyCartEntry) => {
  const quantity = entry.quantity || 1;
  const amount = priceForCartEntry(entry);
  const isOfferDiscount = entry.type === CartEntryType.offerDiscount;
  const shouldShowPrice = entry.price !== null && !isOfferDiscount;

  // The item name is baked in as a plain string by the server at order-placement time and
  // never re-resolved afterward, so a guest who places an order in one language and later
  // switches the app's language keeps seeing the old language on past orders forever.
  // `useComposeDescription` already runs the legacy Sanity lookup to compute the modifier
  // descriptions below — reuse its resolved name instead of issuing a second, identical query.
  // Deliberately does NOT pass allowOneMenuRelocalization: this modal shows a past order that
  // may have been placed at a different store than the one currently selected, and
  // `useEnableUnifiedMenu()`/indexedMenu reflect only the currently selected store — relocalizing
  // against the wrong store's menu could silently substitute a different item/modifier name
  // that happens to share the same sanityId, so One Menu orders keep their legacy-only (or
  // unresolved) fresh-name behavior here rather than risk that.
  const { descriptions: cartEntryDescription, freshName } = useComposeDescription(entry, {
    includeModifiers: true,
  });
  const name = freshName ?? entry.name;

  return (
    <Box style={styles.item} accessible focusable>
      <Box style={styles.itemHeader}>
        <Typography variant="headerFour" style={styles.itemName}>
          {quantity > 1 && `${quantity}x `}
          {name}
        </Typography>
        <Box style={styles.itemPrice}>
          {shouldShowPrice && (
            <Currency
              amount={amount}
              textProps={{
                ...(amount === 0 && { color: getDesignToken('icon-default') }),
                variant: 'headerFour',
                style: { margin: 0 },
                textAlign: 'right',
              }}
            />
          )}
        </Box>
      </Box>

      {cartEntryDescription?.length > 0 &&
        cartEntryDescription.map((descriptionItem, index) => (
          <Typography
            variant="copyTwo"
            style={styles.entryDescriptionRow}
            key={`descriptionItem-${index}`}
          >
            {descriptionItem}
          </Typography>
        ))}
    </Box>
  );
};

export default CartEntry;

const styles = StyleSheet.create({
  entryDescriptionRow: {
    margin: 0,
    width: '100%',
  },
  item: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: spacing.$1,
    padding: 0,
  },
  itemHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.$1,
    width: '100%',
  },
  itemName: {
    flexBasis: '80%',
    flexShrink: 1,
    margin: 0,
  },
  itemPrice: {
    alignItems: 'flex-end',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    width: '16.666%',
  },
});
